import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { DateTime } from '../../sharedComponents/table/BaseTableComponents';
import { NoValue } from '../../sharedComponents/NoValue';
import { Address } from '../../sharedComponents/common.types';
import { TransportCapacityOrder } from '../transportAssignment.types';
import { PropsWithChildren } from 'react';

export const AddressField = (props: { address: Address }) => (
    <div>
        <span className="text-medium ellipsis-2">{`${props.address.name} `}</span>{' '}
        <span>{`${props.address.postalCode} ${props.address.city}`}</span>
    </div>
);

export const IconCell = (props: { icon: string; formattedMessageId: string; nr: number }) => (
    <>
        <span className={classNames('rioglyph', 'text-size-18', 'padding-right-5', props.icon)} />
        <span className="text-bold">{props.nr === 0 ? <>&mdash;&nbsp;</> : props.nr.toString(10)}</span>{' '}
        <FormattedMessage id={props.formattedMessageId} />
    </>
);

export const TransportOrderCapacityOrderVehiclesCell = (props: { transportCapacityOrder: TransportCapacityOrder }) => {
    const numberOfVehicles = props.transportCapacityOrder.requestedTransportCapacity;

    if (numberOfVehicles === 0) {
        return <NoValue />;
    } else {
        const name =
            numberOfVehicles === 1
                ? 'outboundPortal.transportAssignments.table.vehicle'
                : 'outboundPortal.transportAssignments.table.vehicles';
        return (
            <div>
                <IconCell icon="rioglyph-group" nr={numberOfVehicles} formattedMessageId={name} />
            </div>
        );
    }
};

export const CellWithCancellationState = (
    props: PropsWithChildren<{
        isCancelled: boolean;
    }>,
) => <div className={classNames({ 'text-decoration-line-through': props.isCancelled })}>{props.children}</div>;

export const NoValueForSelectableUnloadingAddress = (props: { hasSelectableUnloadingAddress: boolean }) => (
    <NoValue
        messageId={
            props.hasSelectableUnloadingAddress
                ? 'outboundPortal.transportAssignments.table.to.enterDestination'
                : undefined
        }
    />
);

export type DateWithTimezone = { date: Date | undefined; timeZone: string };

const allTheSame = (entries: any[]) => {
    if (entries.length === 0) {
        return true;
    }

    const reference = JSON.stringify(entries[0]);
    return !entries.some((entry) => JSON.stringify(entry) !== reference);
};

export const MultiDateCell = ({ dates, isCancelled }: { dates: DateWithTimezone[]; isCancelled?: boolean }) => {
    const oneValue = allTheSame(dates);

    if (!oneValue) {
        const iconCell = (
            <IconCell
                icon="rioglyph-calendar"
                nr={dates.length}
                formattedMessageId="outboundPortal.transportAssignments.table.dates"
            />
        );

        if (isCancelled) {
            return <CellWithCancellationState isCancelled>{iconCell}</CellWithCancellationState>;
        } else {
            return (
                <OverlayTrigger
                    placement="bottom-start"
                    overlay={
                        <Tooltip textAlignment="left" width="auto">
                            {dates.map((d, idx) => (
                                <div key={idx}>
                                    {d.date !== undefined ? (
                                        <DateTime dateTime={d.date} timeZone={d.timeZone} />
                                    ) : (
                                        <NoValue />
                                    )}
                                </div>
                            ))}
                        </Tooltip>
                    }
                >
                    <div>{iconCell}</div>
                </OverlayTrigger>
            );
        }
    }

    const first = dates[0];
    if (first?.date !== undefined) {
        return (
            <CellWithCancellationState isCancelled={Boolean(isCancelled)}>
                <DateTime dateTime={first.date} timeZone={first.timeZone} />
            </CellWithCancellationState>
        );
    }
    return <NoValue />;
};
